import React, { useState, useEffect } from "react";
import {
  Row,
  Column,
  FormGroup,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  TextInput,
  Checkbox,
} from "carbon-components-react";
import useAuth from "../../hooks/useAuth";
import { Controller } from "react-hook-form";

const WorkersForm = ({
  disabled,
  register,
  control,
  errors,
  hasBusinessCurrently,
  setHasBusinessCurrently,
  getValues,
  businessPlan,
}) => {
  const [stats, setStats] = useState([]);

  // State for current workers totals
  const [currentMalesTotal, setCurrentMalesTotal] = useState(0);
  const [currentFemalesTotal, setCurrentFemalesTotal] = useState(0);

  // State for 2025 workers totals
  const [males2025Total, setMales2025Total] = useState(0);
  const [females2025Total, setFemales2025Total] = useState(0);

  // State for 2026 workers totals
  const [males2026Total, setMales2026Total] = useState(0);
  const [females2026Total, setFemales2026Total] = useState(0);

  // State for 2027 workers totals
  const [males2027Total, setMales2027Total] = useState(0);
  const [females2027Total, setFemales2027Total] = useState(0);

  // Helper: get numeric value from RHF; if absent, fall back to businessPlan.workers
  const getValue = (field) => {
    const value = getValues ? getValues(field) : undefined;
    if (value !== undefined && value !== null && !isNaN(parseFloat(value))) {
      return parseFloat(value);
    }
    // fallback to backend-loaded data during edit/view
    if (businessPlan && businessPlan.workers) {
      const pathParts = (field || "").split(".");
      const key = pathParts.length > 1 ? pathParts[1] : pathParts[0];
      const backendValue = businessPlan.workers[key];
      if (
        backendValue !== undefined &&
        backendValue !== null &&
        !isNaN(parseFloat(backendValue))
      ) {
        return parseFloat(backendValue);
      }
    }
    return 0;
  };

  // Calculate current workers totals
  const calcCurrentTotals = () => {
    const males18To35 = getValue("workers.currentMalesAged18To35");
    const malesAbove35 = getValue("workers.currentMalesAbove35");
    const females18To35 = getValue("workers.currentFemalesAged18To35");
    const femalesAbove35 = getValue("workers.currentFemalesAbove35");

    setCurrentMalesTotal(males18To35 + malesAbove35);
    setCurrentFemalesTotal(females18To35 + femalesAbove35);
  };

  // Calculate 2025 workers totals
  const calc2025Totals = () => {
    const males = getValue("workers.males2025");
    const malesAged = getValue("workers.males2025Aged18To35");
    const females = getValue("workers.females2025");
    const femalesAged = getValue("workers.females2025Aged18To35");

    setMales2025Total(males + malesAged);
    setFemales2025Total(females + femalesAged);
  };

  // Calculate 2026 workers totals
  const calc2026Totals = () => {
    const males = getValue("workers.males2026");
    const malesAged = getValue("workers.males2026Aged18To35");
    const females = getValue("workers.females2026");
    const femalesAged = getValue("workers.females2026Aged18To35");

    setMales2026Total(males + malesAged);
    setFemales2026Total(females + femalesAged);
  };

  // Calculate 2027 workers totals
  const calc2027Totals = () => {
    const males = getValue("workers.males2027");
    const malesAged = getValue("workers.males2027Aged18To35");
    const females = getValue("workers.females2027");
    const femalesAged = getValue("workers.females2027Aged18To35");

    setMales2027Total(males + malesAged);
    setFemales2027Total(females + femalesAged);
  };

  // Handle input blur to recalculate totals
  const handleCurrentBlur = () => {
    calcCurrentTotals();
  };

  const handle2025Blur = () => {
    calc2025Totals();
  };

  const handle2026Blur = () => {
    calc2026Totals();
  };

  const handle2027Blur = () => {
    calc2027Totals();
  };

  // Calculate totals on component mount and when values change
  useEffect(() => {
    calcCurrentTotals();
    calc2025Totals();
    calc2026Totals();
    calc2027Totals();
  }, []);

  // Recalculate when businessPlan loads/changes (edit/view)
  useEffect(() => {
    if (businessPlan) {
      calcCurrentTotals();
      calc2025Totals();
      calc2026Totals();
      calc2027Totals();
    }
  }, [businessPlan]);

  return (
    <FormGroup
      legendText="4	COLABORADORES E POSTOS DE TRABALHO CRIADOS"
      className="section-header"
    >
      <h6 className="section-subtitle">
        A ser preenchida por todos/as candidatos/as independentemente se tem ou
        não um negócio actualmente.
      </h6>

      <fieldset className="bx--fieldset required">
        <legend className="bx--label">
          4.1. Tipo de contrato com os colaboradores
        </legend>
        <div className="bx--form__helper-text">
          [Instrução de preenchimento: Preencha os dados indicando o número
          total de colaboradores,{" "}
          <strong>
            incluindo a si, se for aplicável e o tipo de contracto estabelecido
          </strong>{" "}
          ]
        </div>
        <Row>
          <Column sm={12} md={6} lg={6}>
            <TextInput
              id="totalCollaborators"
              labelText="Número Total de Colaboradores"
              maxLength={12}
              disabled={disabled}
              {...register("workers.totalCollaborators")}
            />
            <p className="field-error">
              {errors.workers?.totalCollaborators?.message}
            </p>
          </Column>
        </Row>
        <Row>
          <Column sm={12} md={4} lg={4}>
            <Controller
              name="workers.formalContract"
              control={control}
              render={({ field }) => (
                <Checkbox
                  labelText="Contrato Formal"
                  id="formalContract"
                  disabled={disabled}
                  checked={field.value || false}
                  onChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </Column>
          <Column sm={12} md={4} lg={4}>
            <Controller
              name="workers.oralContract"
              control={control}
              render={({ field }) => (
                <Checkbox
                  labelText="Contrato Oral"
                  id="oralContract"
                  disabled={disabled}
                  checked={field.value || false}
                  onChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </Column>
        </Row>
      </fieldset>

      <fieldset className="bx--fieldset required">
        <legend className="bx--label">
          4.2 Número actual de colaboradores{" "}
        </legend>
        <div className="bx--form__helper-text">
          [Preencha os dados requeridos na tabela abaixo relativos aos
          colaboradores (incluindo os proprietários/as - trabalhadores/as, e
          nunca usando nomes de pessoas]
        </div>
        <table className="table table-bordered ">
          <thead className="thead-light">
            <tr>
              <th scope="col">Descrição</th>
              <th scope="col">Homens</th>
              <th scope="col">Mulheres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Número actual total de trabalhadores na faixa etária dos 18 a 35
                anos perfazem o número de:
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="currentMalesAged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.currentMalesAged18To35")}
                  onBlur={handleCurrentBlur}
                />
                <p className="field-error">
                  {errors.workers?.currentMalesAged18To35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="currentFemalesAged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.currentFemalesAged18To35")}
                  onBlur={handleCurrentBlur}
                />
                <p className="field-error">
                  {errors.workers?.currentFemalesAged18To35?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                Número actual total de trabalhadores na faixa etária acima de 35
                anos perfazem o número de:
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="currentMalesAbove35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.currentMalesAbove35")}
                  onBlur={handleCurrentBlur}
                />
                <p className="field-error">
                  {errors.workers?.currentMalesAbove35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="currentFemalesAbove35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.currentFemalesAbove35")}
                  onBlur={handleCurrentBlur}
                />
                <p className="field-error">
                  {errors.workers?.currentFemalesAbove35?.message}
                </p>
              </td>
            </tr>
            <tr className="table-secondary">
              <td>
                <strong>TOTAL GERAL</strong>
              </td>
              <td className="text-right">
                <strong>{currentMalesTotal}</strong>
              </td>
              <td className="text-right">
                <strong>{currentFemalesTotal}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </fieldset>

      <fieldset className="bx--fieldset required">
        <legend className="bx--label">4.3 Pessoal Necessário até 2027 </legend>
        <div className="bx--form__helper-text">
          Preencha os dados requeridos na tabela abaixo relativos aos
          colaboradores (incluindo os proprietários/as - trabalhadores/as, e
          nunca usando nomes de pessoas]
        </div>
        <table className="table table-bordered ">
          <thead className="thead-light">
            <tr>
              <th scope="col">Descrição</th>
              <th scope="col">Genero</th>
              <th scope="col">2025</th>
              <th scope="col">2026</th>
              <th scope="col">2027</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td rowSpan={2}>
                Número total de trabalhadores na faixa etária dos 18 a 35 anos
                perfazem o número de:
              </td>
              <td>Homens</td>
              <td>
                <TextInput
                  labelText=""
                  id="males2025"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2025")}
                  onBlur={handle2025Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2025?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="males2026"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2026")}
                  onBlur={handle2026Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2026?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="males2027"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2027")}
                  onBlur={handle2027Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2027?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Mulheres</td>
              <td>
                <TextInput
                  labelText=""
                  id="males2025Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2025Aged18To35")}
                  onBlur={handle2025Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2025Aged18To35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="males2026Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2026Aged18To35")}
                  onBlur={handle2026Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2026Aged18To35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="males2027Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.males2027Aged18To35")}
                  onBlur={handle2027Blur}
                />
                <p className="field-error">
                  {errors.workers?.males2027Aged18To35?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td rowSpan={2}>
                Número total de trabalhadores na faixa etária acima dos 35 anos
                perfazem o número de:
              </td>
              <td>Homens</td>
              <td>
                <TextInput
                  labelText=""
                  id="females2025"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2025")}
                  onBlur={handle2025Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2025?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="females2026"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2026")}
                  onBlur={handle2026Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2026?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="females2027"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2027")}
                  onBlur={handle2027Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2027?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Mulheres</td>
              <td>
                <TextInput
                  labelText=""
                  id="females2025Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2025Aged18To35")}
                  onBlur={handle2025Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2025Aged18To35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="females2026Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2026Aged18To35")}
                  onBlur={handle2026Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2026Aged18To35?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="females2027Aged18To35"
                  maxLength={12}
                  disabled={disabled}
                  {...register("workers.females2027Aged18To35")}
                  onBlur={handle2027Blur}
                />
                <p className="field-error">
                  {errors.workers?.females2027Aged18To35?.message}
                </p>
              </td>
            </tr>
            <tr className="table-secondary">
              <td colSpan={2}>
                <strong>TOTAL GERAL</strong>
              </td>
              <td className="text-right">
                <strong>{males2025Total + females2025Total}</strong>
              </td>
              <td className="text-right">
                <strong>{males2026Total + females2026Total}</strong>
              </td>
              <td className="text-right">
                <strong>{males2027Total + females2027Total}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </fieldset>
    </FormGroup>
  );
};

WorkersForm.defaultProps = {
  disabled: false,
};
export default WorkersForm;
